// Complete Fixed AuthContext.js - With Single Device Login Support and Improvements
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { setupApiTracking } from '../utils/tracking';

// ---- Session expiry helpers (shared with services/api.js, services/stocksApi.js, App.js)
const AUTH_API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Endpoints whose 401 means "bad credentials", not "session expired".
const AUTH_ENDPOINTS = /\/auth\/(login|register|refresh|forgot-password|reset-password|confirm-email|logout|google)/;

const AUTH_KEYS = [
  'access_token',
  'refresh_token',
  'currentUser',
  'session_info',
  'session_expires',
  'subscriptionInfo',
  'subscriptionCheckedAt',
  'registrationComplete',
];

let refreshPromise = null;
let redirecting = false;

export const isAuthEndpoint = (url = '') => AUTH_ENDPOINTS.test(url);

export const clearStoredAuth = () => {
  const rememberMe = localStorage.getItem('rememberMe');
  AUTH_KEYS.forEach((k) => localStorage.removeItem(k));
  if (rememberMe) localStorage.setItem('rememberMe', rememberMe);
  sessionStorage.removeItem('subscription_error');
  sessionStorage.removeItem('pendingSubscription');
};

// Hard navigation (full reload) so in-memory React state + module caches reset.
export const forceLogin = (reason = 'session_expired') => {
  clearStoredAuth();
  if (redirecting) return;
  redirecting = true;
  sessionStorage.setItem('session_reason', reason);
  sessionStorage.setItem(
    'session_message',
    reason === 'another_device'
      ? 'You have been logged out because you logged in from another device'
      : 'Your session has expired. Please login again.'
  );
  if (!window.location.pathname.startsWith('/login')) {
    window.location.replace(`/login?reason=${encodeURIComponent(reason)}`);
  }
};

export const isRedirectingToLogin = () => redirecting;

// Single-flight refresh: concurrent 401s share one /api/auth/refresh call.
// Resolves to the new access token, or null if refresh is impossible/failed.
export const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return Promise.resolve(null);

  refreshPromise = axios
    .post(`${AUTH_API_BASE_URL}/api/auth/refresh`, { refresh_token: refreshToken }, { timeout: 15000 })
    .then((res) => {
      const token = res?.data?.access_token;
      if (!token) return null;
      localStorage.setItem('access_token', token);
      return token;
    })
    .catch(() => null)
    .finally(() => {
      setTimeout(() => { refreshPromise = null; }, 0);
    });
  return refreshPromise;
};

// Shared 401 handler for axios instances.
// Returns the retried response on successful refresh; otherwise redirects to
// /login and rejects with an error carrying the original response.
export const handleUnauthorized = async (instance, response) => {
  const config = response?.config || {};
  const err = new Error('Session expired. Please log in again.');
  err.response = response;
  err.config = config;
  err.isAuthExpired = true;

  if (isAuthEndpoint(config.url) || redirecting) throw err;

  if (!config._retry) {
    const token = await refreshAccessToken();
    if (token) {
      config._retry = true;
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
      if (instance?.defaults?.headers?.common) {
        instance.defaults.headers.common.Authorization = `Bearer ${token}`;
      }
      return instance(config);
    }
  }

  const reason = response?.data?.error === 'Session invalid' ? 'another_device' : 'session_expired';
  forceLogin(reason);
  throw err;
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// FIXED: Create axios instance with proper configuration - MUST BE BEFORE INTERCEPTORS
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  timeout: 120000, //  CHANGED: Increased from 30000 to 120000 (2 minutes)
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // Ensure no credential issues
  maxRedirects: 5, //  ADDED: Better redirect handling
  validateStatus: (status) => status < 500, //  ADDED: Don't reject for 4xx errors
});

// Activity tracking v2: tag requests with page/session/visitor headers
setupApiTracking(api);

// FIXED: Enhanced request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // FIXED: Handle API paths correctly to prevent double /api
    if (config.url) {
      // First, check if baseURL already ends with /api
      const baseHasApi = config.baseURL && config.baseURL.endsWith('/api');
      
      if (baseHasApi) {
        // If base already has /api, remove /api from the beginning of the URL if present
        if (config.url.startsWith('/api/')) {
          config.url = config.url.substring(4); // Remove '/api' prefix
        }
      } else {
        // If base doesn't have /api, ensure URL starts with /api
        if (!config.url.startsWith('/api/')) {
          config.url = `/api${config.url.startsWith('/') ? '' : '/'}${config.url}`;
        }
      }
      
      // Additional safety: fix any double /api that might still occur
      const fullUrl = `${config.baseURL}${config.url}`;
      if (fullUrl.includes('/api/api/')) {
        config.url = config.url.replace('/api/', '/');
      }
    }

    //  ENHANCED: Add request timing metadata
    config.metadata = { startTime: Date.now() };

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

//  Helper function to recursively clean NaN values from objects
function cleanObjectNaN(obj) {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => cleanObjectNaN(item));
  }
  
  if (typeof obj === 'object') {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      cleaned[key] = cleanObjectNaN(value);
    }
    return cleaned;
  }
  
  // Handle primitive values
  if (typeof obj === 'number') {
    if (isNaN(obj) || !isFinite(obj)) {
      return null;
    }
  }
  
  return obj;
}

//  CRITICAL FIX: Enhanced response interceptor with subscription expiry and single device login handling
api.interceptors.response.use(
  (response) => {
    //  ENHANCED: Calculate request time
    const requestTime = Date.now() - (response.config.metadata?.startTime || Date.now());

    // FIX (2026-10-08): validateStatus (< 500) resolves 4xx, so the error branch
    // below never saw a 401. An expired JWT was treated as "no subscription",
    // users were bounced to /subscription and stuck on "Verifying your trial...".
    // Handle 401 here: refresh once + retry, else clear auth and go to /login.
    if (response.status === 401 && !isAuthEndpoint(response.config?.url)) {
      return handleUnauthorized(api, response);
    }

    // FIXED: Clean NaN values from response text before parsing
    if (typeof response.data === 'string') {
      try {
        let cleanedData = response.data
          .replace(/:\s*NaN\s*(,|})/g, ':null$1')
          .replace(/:\s*Infinity\s*(,|})/g, ':null$1')
          .replace(/:\s*-Infinity\s*(,|})/g, ':null$1')
          .replace(/"\s*NaN\s*"/g, 'null')
          .replace(/"\s*Infinity\s*"/g, 'null')
          .replace(/"\s*-Infinity\s*"/g, 'null');
        
        response.data = JSON.parse(cleanedData);
    
      } catch (parseError) {
        console.error('AuthContext: Failed to parse JSON response:', parseError);
        response.data = {
          stocks: [],
          total: 0,
          totalPages: 1,
          page: 1,
          pageSize: 100,
          error: 'Response parsing failed due to invalid JSON'
        };
      }
    }
    
    if (response.data && typeof response.data === 'object') {
      response.data = cleanObjectNaN(response.data);
    }
    
    return response;
  },
  async (error) => {
    //  ENHANCED: Calculate request time for errors
    const requestTime = Date.now() - (error.config?.metadata?.startTime || Date.now());
    
    const status = error.response?.status;
    const errorCode = error.response?.data?.code;
    const errorData = error.response?.data;
    
    console.error('AuthContext API Error:', {
      status: status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      errorCode: errorCode,
      errorMessage: errorData?.message,
      timeout: error.code === 'ECONNABORTED',
      networkError: error.message.includes('Network Error'),
      requestTime: requestTime,
      data: errorData,
      message: error.message,
      timestamp: new Date().toISOString()
    });

    const originalRequest = error.config;

    //  CRITICAL FIX: Handle subscription expiry (403) - DON'T logout, redirect to subscription page
    if (status === 403 && (
      errorCode === 'SUBSCRIPTION_EXPIRED' || 
      errorCode === 'TRIAL_EXPIRED' ||
      errorCode === 'NO_SUBSCRIPTION'
    )) {
      console.log('Subscription expired - redirecting to subscription page (tokens preserved)');
      
      // DON'T clear tokens - user is still authenticated, just needs to renew subscription
      // Store subscription error details for the subscription page
      sessionStorage.setItem('subscription_error', JSON.stringify({
        code: errorCode,
        message: errorData?.message || 'Subscription required',
        is_trial: errorData?.is_trial || false,
        expired_at: errorData?.expired_at || null,
        plan_name: errorData?.plan_name || null,
        action_required: errorData?.action_required || null
      }));
      
      //  FIX: Build redirect URL with safe parameter encoding
      const redirectParams = [];
      redirectParams.push('expired=true');
      
      if (errorCode) {
        redirectParams.push(`code=${encodeURIComponent(errorCode)}`);
      }
      
      if (errorData?.message) {
        // Properly encode the message to avoid [object Object]
        const safeMessage = String(errorData.message);
        redirectParams.push(`message=${encodeURIComponent(safeMessage)}`);
      }
      
      if (errorData?.is_trial) {
        redirectParams.push('trial=true');
      }
      
      if (errorData?.expired_at) {
        redirectParams.push(`expired_at=${encodeURIComponent(errorData.expired_at)}`);
      }
      
      if (errorData?.plan_name) {
        redirectParams.push(`plan=${encodeURIComponent(errorData.plan_name)}`);
      }
      
      // Construct the final URL
      const redirectUrl = `/subscription?${redirectParams.join('&')}`;
      
      console.log('Redirecting to:', redirectUrl);
      
      // Redirect to subscription page WITHOUT clearing authentication
      window.location.href = redirectUrl;
      return Promise.reject(error);
    }

    //  Handle single device session errors (different from subscription expiry)
    if (status === 401 && errorData?.error === 'Session invalid') {
      console.log('Session invalidated - logged in from another device');
      
      //  Preserve rememberMe preference
      const rememberMeValue = localStorage.getItem('rememberMe');
      
      // Clear authentication
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('session_info');
      localStorage.removeItem('session_expires');
      
      //  Restore rememberMe
      if (rememberMeValue) {
        localStorage.setItem('rememberMe', rememberMeValue);
      }
      
      const message = errorData?.message || 'You have been logged out because you logged in from another device';
      
      sessionStorage.setItem('session_message', message);
      sessionStorage.setItem('session_reason', 'another_device');
      
      window.location.href = '/login?reason=another_device';
      return Promise.reject(error);
    }

    //  Handle expired session (different from subscription expiry)
    if (status === 401 && errorData?.error === 'Session expired') {
      console.log('Session expired');
      
      //  Preserve rememberMe preference
      const rememberMeValue = localStorage.getItem('rememberMe');
      
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('session_info');
      localStorage.removeItem('session_expires');
      
      //  Restore rememberMe
      if (rememberMeValue) {
        localStorage.setItem('rememberMe', rememberMeValue);
      }
      
      sessionStorage.setItem('session_message', 'Your session has expired. Please login again.');
      sessionStorage.setItem('session_reason', 'session_expired');
      
      window.location.href = '/login?reason=session_expired';
      return Promise.reject(error);
    }

    //  Handle timeout errors
    if (error.code === 'ECONNABORTED') {
      console.error(`Request timeout after ${requestTime}ms`);
      
      let timeoutMessage = 'Request timeout - please try again';
      if (requestTime >= 120000) {
        timeoutMessage = 'Server is taking too long to respond. This may indicate server overload.';
      } else if (requestTime >= 60000) {
        timeoutMessage = 'Request is taking longer than expected. Server may be processing large dataset.';
      }
      
      return Promise.reject(new Error(timeoutMessage));
    }

    //  Handle network connection errors
    if (error.message.includes('Network Error') || error.code === 'ERR_NETWORK') {
      return Promise.reject(new Error('Network connection error. Please check your internet connection.'));
    }

    //  Handle token refresh for 401 errors (authentication failures, NOT subscription issues)
    if (status === 401 && !originalRequest._retry && errorData?.error !== 'Session invalid') {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          console.log('Attempting token refresh...');
          const response = await api.post('/api/auth/refresh', {
            refresh_token: refreshToken,
          });

          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);
          api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          
          console.log('Token refreshed successfully, retrying original request');
          return api(originalRequest);
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          
          //  CRITICAL FIX: Check if refresh failed due to subscription expiry
          const refreshErrorCode = refreshError.response?.data?.code;
          const refreshErrorData = refreshError.response?.data;
          
          if (refreshErrorCode === 'SUBSCRIPTION_EXPIRED' || 
              refreshErrorCode === 'TRIAL_EXPIRED' ||
              refreshErrorCode === 'NO_SUBSCRIPTION') {
            console.log('Token refresh failed due to subscription expiry');
            
            // Store subscription error and redirect to subscription page
            sessionStorage.setItem('subscription_error', JSON.stringify({
              code: refreshErrorCode,
              message: refreshErrorData?.message || 'Subscription required',
              is_trial: refreshErrorData?.is_trial || false,
              expired_at: refreshErrorData?.expired_at || null,
              plan_name: refreshErrorData?.plan_name || null
            }));
            
            //  FIX: Build safe redirect URL
            const redirectParams = [];
            redirectParams.push('expired=true');
            redirectParams.push(`code=${encodeURIComponent(refreshErrorCode)}`);
            redirectParams.push('from_refresh=true');
            
            if (refreshErrorData?.message) {
              const safeMessage = String(refreshErrorData.message);
              redirectParams.push(`message=${encodeURIComponent(safeMessage)}`);
            }
            
            const redirectUrl = `/subscription?${redirectParams.join('&')}`;
            
            window.location.href = redirectUrl;
            return Promise.reject(refreshError);
          }
          
          // Otherwise, it's a genuine auth failure - logout
          //  Preserve rememberMe preference
          const rememberMeValue = localStorage.getItem('rememberMe');
          
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('currentUser');
          localStorage.removeItem('session_info');
          localStorage.removeItem('session_expires');
          
          //  Restore rememberMe
          if (rememberMeValue) {
            localStorage.setItem('rememberMe', rememberMeValue);
          }
          
          sessionStorage.setItem('session_message', 'Your session has expired. Please login again.');
          sessionStorage.setItem('session_reason', 'token_expired');
          
          window.location.href = '/login?reason=token_expired';
          return Promise.reject(new Error('Session expired. Please log in again.'));
        }
      } else {
        console.log('No refresh token available');
        
        //  Preserve rememberMe preference
        const rememberMeValue = localStorage.getItem('rememberMe');
        
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('currentUser');
        localStorage.removeItem('session_info');
        localStorage.removeItem('session_expires');
        
        //  Restore rememberMe
        if (rememberMeValue) {
          localStorage.setItem('rememberMe', rememberMeValue);
        }
        
        window.location.href = '/login';
      }
    }

    //  Handle server errors
    if (status >= 500) {
      const serverErrorMessage = status === 500 
        ? 'Internal server error. The server may be overloaded.' 
        : 'Server is temporarily unavailable. Please try again later.';
      return Promise.reject(new Error(serverErrorMessage));
    }

    //  Handle rate limiting
    if (status === 429) {
      return Promise.reject(new Error('Too many requests. Please wait a moment before trying again.'));
    }

    return Promise.reject(error);
  }
);

const withRetry = async (apiCall, maxRetries = 2, baseDelay = 2000) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (typeof apiCall === 'function') {
        return await apiCall();
      }
      return await apiCall;
    } catch (error) {
      lastError = error;
      
      // Don't retry for certain error types
      const shouldNotRetry = 
        error.response?.status === 401 || // Auth errors
        error.response?.status === 403 || // Forbidden
        error.response?.status === 404 || // Not found
        error.response?.status === 422 || // Validation errors
        error.message.includes('Session expired') ||
        error.message.includes('Session invalid') || //  NEW: Don't retry for session errors
        error.message.includes('another device'); //  NEW: Don't retry for device conflicts
      
      if (shouldNotRetry || attempt === maxRetries) {
        throw error;
      }
      
      // Only retry for timeout, network, or server errors
      const shouldRetry = 
        error.code === 'ECONNABORTED' ||
        error.message.includes('timeout') ||
        error.message.includes('Network Error') ||
        error.response?.status >= 500;
      
      if (!shouldRetry) {
        throw error;
      }
      
      // Exponential backoff with jitter
      const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
      console.log(`Retrying API call in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  const clearAuthState = useCallback(() => {
    console.log('Clearing all authentication state');
    
    //  Preserve rememberMe preference
    const rememberMeValue = localStorage.getItem('rememberMe');
    
    // Clear all auth-related items from localStorage
    const authKeys = [
      'access_token',
      'refresh_token', 
      'currentUser',
      'session_info',
      'session_expires',
      'subscriptionInfo',
      'subscriptionCheckedAt',
      'registrationComplete'
    ];
    
    authKeys.forEach(key => localStorage.removeItem(key));
    clearStoredAuth();
    
    //  Restore rememberMe
    if (rememberMeValue) {
      localStorage.setItem('rememberMe', rememberMeValue);
    }
    
    // Clear sessionStorage
    sessionStorage.removeItem('pendingSubscription');
    sessionStorage.removeItem('session_message');
    sessionStorage.removeItem('session_reason');
    
    // Reset state
    setCurrentUser(null);
    setHasActiveSubscription(false);
    setError(null);
    setLoading(false);
    
    // Clear axios headers
    delete api.defaults.headers.common['Authorization'];
  }, []);

  const checkForPotentialAutoApproval = useCallback((user) => {
    if (!user) return false;
    
    // If user is pending but has active subscription, they should be auto-approved
    if (user.approval_status === 'pending' && user.subscription) {
      const validStatuses = ['active', 'trialing', 'paid', 'trial'];
      const hasActiveSubscription = validStatuses.includes(user.subscription.status);
      
      if (hasActiveSubscription) {
        console.log(' AuthContext: User has active subscription but is pending - should be auto-approved');
        return true;
      }
    }
    
    return false;
  }, []);

  //  NEW: Validate user state
  const validateUserState = useCallback((user) => {
    if (!user || typeof user !== 'object') {
      return { isValid: false, reason: 'User object is null or invalid' };
    }
    
    // FIXED: email is NOT required, handle it gracefully
    const requiredFields = ['id', 'username'];  // Removed 'email' from required
    for (const field of requiredFields) {
      if (!user[field]) {
        return { isValid: false, reason: `Missing required field: ${field}` };
      }
    }
    
    // ADDED: Ensure email is always a string, never null
    if (!user.email || user.email === null || user.email === undefined) {
      // Check if username is email format
      if (user.username && user.username.includes('@')) {
        user.email = user.username;

      } else {
        user.email = "";

      }
    }
    
    // Validate approval status
    const validStatuses = ['pending', 'approved', 'rejected'];
    if (user.approval_status && !validStatuses.includes(user.approval_status)) {
      return { isValid: false, reason: 'Invalid approval status' };
    }
    
    // Auto-fix inconsistent approval state
    if (user.approval_status === 'approved' && user.is_approved === false) {
      console.warn(' AuthContext: Inconsistent approval state detected, auto-fixing...');
      user.is_approved = true;
    }
    
    // Check for potential auto-approval scenario
    if (checkForPotentialAutoApproval(user)) {
      console.log(' AuthContext: User should potentially be auto-approved');
    }
    
    return { isValid: true, user };
  }, [checkForPotentialAutoApproval]);
  
  const checkSubscription = async (forceRefresh = false) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token || !currentUser) {
        return false;
      }
      
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      
      if (forceRefresh) {
        headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
        headers['Pragma'] = 'no-cache';
      }
      
      const response = await api.get('/api/subscription/status', {
        headers,
        params: forceRefresh ? { _t: Date.now() } : {}
      });
      
      if (response.status === 401) {
        return false; // interceptor already refreshed or redirected to /login
      }

      if (response.status === 200) {
        const data = response.data;
        const isActive = data.is_active || false;
        const hasSubscription = data.has_subscription || false;
        
        // Update state
        setHasActiveSubscription(isActive);
        
        // Update currentUser with fresh subscription data
        if (data.subscription && currentUser) {
          // FIXED: Ensure email is preserved when updating user
          const updatedUser = {
            ...currentUser,
            email: currentUser.email || "",  // Ensure email stays as string
            subscription: data.subscription
          };
          
          setCurrentUser(updatedUser);
          localStorage.setItem('currentUser', JSON.stringify(updatedUser));
        }
        
        // Store subscription info in localStorage for backup
        if (data.subscription) {
          localStorage.setItem('subscriptionInfo', JSON.stringify(data.subscription));
        }
        
        return isActive;
      }
      
      return false;
    } catch (error) {
      console.error(' AuthContext: Error checking subscription:', error);
      
      if (error.response?.status === 404) {
        console.error(' AuthContext: /api/subscription/status endpoint not found! Check backend implementation.');
      }
      
      return false;
    }
  };

  //  NEW: Start trial subscription
  const startTrial = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Not authenticated');

      const response = await api.post('/api/subscription/trial/start');

      if (response.status !== 201) {
        const error = response.data;
        throw new Error(error.error || 'Failed to start trial');
      }

      const data = response.data;
      
      // Update subscription status
      await checkSubscription();
      
      return data;
    } catch (error) {
      console.error('Error starting trial:', error);
      throw error;
    }
  };

  //  NEW: Get Paddle subscription status
  const getPaddleSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Not authenticated');

      const response = await api.get('/api/paddle/subscription-status');

      if (response.status !== 200) {
        throw new Error('Failed to get Paddle subscription status');
      }

      return response.data;
    } catch (error) {
      console.error('Error getting Paddle subscription:', error);
      throw error;
    }
  };
  
  //  NEW: Cancel Paddle subscription
  const cancelSubscription = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Not authenticated');

      const response = await api.post('/api/paddle/cancel-subscription');

      if (response.status !== 200) {
        const error = response.data;
        throw new Error(error.error || 'Failed to cancel subscription');
      }

      const data = response.data;
      
      // Update subscription status
      await checkSubscription();
      
      return data;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  };

  // Enhanced logout with proper string handling
  const logout = useCallback((redirectMessage = null) => {
    console.log('Logging out user');
    
    // Clear all state and storage
    clearAuthState();
    
    // Optional: Call logout endpoint to invalidate server session
    api.post('/api/auth/logout').catch(err => {
      console.log('Logout API call failed (non-critical):', err);
    });
    
    // Build redirect URL properly - NO objects, only strings.
    // Send the user to the public home page after logout (not /login).
    // from_logout=true lets the landing page show a one-click "Sign in" prompt.
    let redirectUrl = '/?from_logout=true';
    
    // Add message if provided (must be a string)
    if (redirectMessage && typeof redirectMessage === 'string') {
      redirectUrl += `&message=${encodeURIComponent(redirectMessage)}`;
    }
    
    console.log('Redirecting to:', redirectUrl);
    
    // Redirect
    window.location.href = redirectUrl;
  }, [clearAuthState]);

  const fetchUserProfile = async (forceRefresh = false) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.warn(' AuthContext: No access token available');
        throw new Error('No authentication token');
      }
  
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      
      if (forceRefresh) {
        headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
        headers['Pragma'] = 'no-cache';
        headers['Expires'] = '0';
      }
      
      const response = await api.get('/api/auth/me', {
        headers,
        params: forceRefresh ? { _t: Date.now() } : {}
      });
      
      if (response.status !== 200 || !response.data?.user) {
        const err = new Error(response.data?.error || `Profile request failed (${response.status})`);
        err.response = response;
        throw err;
      }

      const userData = response.data.user;
      
      // FIXED: Ensure email is handled properly
      if (!userData.email || userData.email === "") {
        console.warn(' AuthContext: User profile missing email');
        if (userData.username && userData.username.includes('@')) {
          userData.email = userData.username;
          console.log(' Using username as email:', userData.username);
        } else {
          userData.email = "";
        }
      }
      
      // Check for approval status changes
      if (currentUser?.approval_status === 'pending' && userData.approval_status === 'approved') {
        console.log(' User was auto-approved!');
        sessionStorage.removeItem('pendingApproval');
      }
      
      // Validate and set user data
      const validation = validateUserState(userData);
      if (validation.isValid) {
        setCurrentUser(validation.user);
        localStorage.setItem('currentUser', JSON.stringify(validation.user));
        
        // Update subscription status if available
        if (validation.user.subscription) {
          const isActive = checkIfSubscriptionActive(validation.user.subscription);
          setHasActiveSubscription(isActive);

        }
        
        return validation.user;
      } else {
        console.error(' User validation failed:', validation.reason);
        throw new Error(`Invalid user data: ${validation.reason}`);
      }
    } catch (error) {
      console.error(' AuthContext: Error fetching user profile:', error);
      
      if (error.response?.status === 401) {
        console.log(' Token expired, clearing auth state');
        clearAuthState();
        forceLogin('session_expired');
      } else if (error.response?.status === 403) {
        const errorData = error.response.data;
        if (errorData?.approval_status && currentUser) {
          const updatedUser = {
            ...currentUser,
            email: currentUser.email || "",  // Preserve email
            approval_status: errorData.approval_status
          };
          setCurrentUser(updatedUser);
          localStorage.setItem('currentUser', JSON.stringify(updatedUser));
        }
      }
      
      throw error;
    }
  };

  const checkIfSubscriptionActive = (subscription) => {
    if (!subscription) return false;
    
    const now = new Date();
    const expiresAt = subscription.expires_at ? new Date(subscription.expires_at) : null;
    const isExpired = expiresAt ? now > expiresAt : false;
    // FIXED: Recognize 'trial' as a valid active status
    const validStatuses = ['active', 'trial', 'trialing', 'paid'];
    const isActive = validStatuses.includes(subscription.status) && !isExpired;
    
    return isActive;
  };

  const refreshSubscriptionStatus = async () => {
    try {
      const response = await api.get('/api/subscription/status');
      
      if (response.data.subscription) {
        // Update the current user with fresh subscription data
        if (currentUser) {
          const updatedUser = {
            ...currentUser,
            subscription: response.data.subscription
          };
          
          setCurrentUser(updatedUser);
          localStorage.setItem('currentUser', JSON.stringify(updatedUser));
          
          // Update subscription active state
          // FIXED: Recognize 'trial' as a valid active status
          const validStatuses = ['active', 'trial', 'trialing', 'paid'];
          const isActive = validStatuses.includes(response.data.subscription.status) &&
                           (!response.data.subscription.expires_at || 
                            new Date(response.data.subscription.expires_at) > new Date());
          
          setHasActiveSubscription(isActive);
          
          return response.data.subscription;
        }
      }
      
      return null;
    } catch (error) {
      console.error(' AuthContext: Error refreshing subscription:', error);
      throw error;
    }
  };

  //  IMPROVED: Enhanced initialization with validation
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const userStr = localStorage.getItem('currentUser');
        
        if (!token || !userStr) {
          setLoading(false);
          return;
        }
        
        let parsedUser;
        try {
          parsedUser = JSON.parse(userStr);
        } catch (parseError) {
          console.error('Failed to parse stored user data:', parseError);
          clearAuthState();
          setLoading(false);
          return;
        }
        
        // Validate user data
        const validation = validateUserState(parsedUser);
        if (!validation.isValid) {
          console.warn('Invalid stored user data:', validation.reason);
          clearAuthState();
          setLoading(false);
          return;
        }
        
        // Set validated user
        setCurrentUser(validation.user);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Try to fetch fresh user profile
        try {
          await fetchUserProfile();
          await checkSubscription();
        } catch (profileError) {
          console.error('Failed to fetch user profile:', profileError);
          // If profile fetch fails with 401, clear auth
          if (profileError.response?.status === 401 || profileError.isAuthExpired) {
            clearAuthState();
            forceLogin('session_expired');
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        clearAuthState();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []); // Dependencies removed to prevent loops

  const login = async (username, password, rememberMe = false) => {
    setLoading(true);
    setError(null);
  
    try {
      console.log('Attempting login for:', username, 'Remember me:', rememberMe);
      
      const response = await api.post('/api/auth/login', {
        username,
        password,
        remember_me: rememberMe
      });
  
      // Check if this is a 403 subscription error
      if (response.status === 403) {
        const data = response.data;
        console.log('Login blocked due to subscription issue:', data);
        
        // Handle different subscription error codes
        if (data.code === 'TRIAL_EXPIRED') {
          const message = `Your free trial expired on ${new Date(data.expired_at).toLocaleDateString()}. Please subscribe to continue.`;
          setError(message);
          
          setTimeout(() => {
            const params = new URLSearchParams({
              expired: 'true',
              code: 'TRIAL_EXPIRED',
              from: 'login',
              trial_expired_at: data.expired_at
            });
            window.location.href = `/subscription?${params.toString()}`;
          }, 2000);
          
          throw new Error(message);
        } 
        else if (data.code === 'SUBSCRIPTION_EXPIRED') {
          const message = `Your subscription expired. Please renew to continue.`;
          setError(message);
          
          setTimeout(() => {
            const params = new URLSearchParams({
              expired: 'true',
              code: 'SUBSCRIPTION_EXPIRED',
              from: 'login'
            });
            window.location.href = `/subscription?${params.toString()}`;
          }, 2000);
          
          throw new Error(message);
        }
        else if (data.code === 'NO_SUBSCRIPTION') {
          const message = 'An active subscription is required. Please subscribe to continue.';
          setError(message);
          
          setTimeout(() => {
            window.location.href = '/subscription?from=login';
          }, 2000);
          
          throw new Error(message);
        }
        else if (data.approval_status === 'pending') {
          const message = 'Your account is pending approval.';
          setError(message);
          throw new Error(message);
        }
        else {
          const message = data.message || data.error || 'Your account is not active.';
          setError(message);
          throw new Error(message);
        }
      }
  
      if (response.status === 401 || response.status === 400) {
        const message = response.data?.error || response.data?.message || 'Invalid username or password.';
        setError(message);
        throw new Error(message);
      }

      // Validate successful login response
      if (!response || !response.data) {
        console.error('Invalid response structure:', response);
        throw new Error('Invalid server response');
      }
  
      const responseData = response.data;
      
      if (!responseData.access_token) {
        console.error('Missing access_token in response:', responseData);
        throw new Error('Server error: missing access token');
      }
  
      if (!responseData.user) {
        console.error('Missing user object in response:', responseData);
        throw new Error('Server error: missing user data');
      }
  
      if (!responseData.user.id) {
        console.error('User object missing id:', responseData.user);
        throw new Error('Server error: user data incomplete');
      }
  
      // Safely destructure
      const { access_token, refresh_token, user, session_info } = responseData;
  
      // Store tokens and user data
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('currentUser', JSON.stringify(user));
      
      if (session_info) {
        localStorage.setItem('session_info', JSON.stringify(session_info));
        localStorage.setItem('session_expires', session_info.session_expires);
        
        // Log session info for user awareness
        if (session_info.is_paid_user && session_info.remember_me) {
          console.log('Extended session: You will stay logged in for', session_info.session_duration);
        }
      }
      
      setCurrentUser(user);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
  
      // Check subscription status after login (non-blocking, don't throw errors)
      try {
        await checkSubscription();
      } catch (subError) {
        console.warn('Subscription check failed (non-critical):', subError);
        // Don't throw - subscription check is not critical for login completion
      }
  
      console.log('Login successful:', {
        userId: user.id,
        username: user.username,
        approval_status: user.approval_status,
        is_approved: user.is_approved,
        hasSubscription: !!user.subscription,
        sessionDuration: session_info?.session_duration
      });
  
      return { user, access_token, refresh_token, session_info };
      
    } catch (err) {
      console.error('Login error:', err);
  
      let errorMessage = 'Login failed. Please try again.';
  
      // If error already has a message we set, use it
      if (err.message && err.message !== 'Login failed. Please try again.') {
        setError(err.message);
        throw err;
      }
  
      if (err.response) {
        const { status, data } = err.response;
        
        console.error('Server responded with status:', status);
        console.error('Server error data:', data);
        
        if (status === 401) {
          errorMessage = data?.error || 'Invalid username or password.';
        } else if (status === 403) {
          errorMessage = data?.message || data?.error || 'Access denied.';
        } else if (status === 400) {
          errorMessage = data?.error || 'Invalid login data.';
        } else if (status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = data?.error || data?.message || 'Login failed.';
        }
      } else if (err.request) {
        console.error('No response received from server');
        errorMessage = 'Could not connect to server. Please check your internet connection.';
      }
  
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const register = async (registrationData) => {
    setLoading(true);
    setError(null);
  
    try {
      const requiredFields = ['username', 'email', 'password', 'first_name', 'last_name'];
      for (const field of requiredFields) {
        if (!registrationData[field] || !registrationData[field].trim()) {
          throw new Error(`${field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} is required`);
        }
      }
  
      console.log(' AuthContext: Sending registration request:', {
        ...registrationData,
        password: '[HIDDEN]'
      });
  
      const response = await api.post('/api/auth/register', registrationData);
      
      console.log(' AuthContext: Registration response received:', response.data);
  
      // Handle registration response with subscription info
      const responseData = response.data;
      const message = responseData.message;
      const subscriptionInfo = responseData.subscription;
      
      let user;
      
      // Handle different response formats from backend
      if (responseData.user) {
        // Format 1: Backend returns full user object
        user = responseData.user;
      } else if (responseData.user_id && responseData.username) {
        // Format 2: Backend returns user_id and username separately
        user = {
          id: responseData.user_id,
          username: responseData.username,
          email: registrationData.email,
          first_name: registrationData.first_name,
          last_name: registrationData.last_name,
          approval_status: responseData.status === 'pending_approval' ? 'pending' : 'pending',
          is_approved: false,
          roles: ['basic']
        };
      } else {
        // Format 3: Minimal response - construct user from registration data
        user = {
          id: responseData.user_id || null,
          username: registrationData.username,
          email: registrationData.email,
          first_name: registrationData.first_name,
          last_name: registrationData.last_name,
          approval_status: 'pending',
          is_approved: false,
          roles: ['basic']
        };
      }
  
      console.log(' AuthContext: Processed user object:', user);
  
      // Set the current user state - this is CRITICAL for routing
      if (user) {
        const userToSet = {
          id: user.id,
          username: user.username,
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          approval_status: user.approval_status || 'pending',
          is_approved: user.is_approved || false,
          roles: user.roles || ['basic'],
          subscription_status: user.subscription_status || 'none',
          has_trial_used: user.has_trial_used || false
        };
  
        console.log(' AuthContext: Setting currentUser state:', userToSet);
        
        setCurrentUser(userToSet);
        localStorage.setItem('currentUser', JSON.stringify(userToSet));
  
        // Store subscription info if provided
        if (subscriptionInfo) {
          localStorage.setItem('subscriptionInfo', JSON.stringify(subscriptionInfo));
          console.log(' AuthContext: Subscription info stored:', subscriptionInfo);
        }

        //  NEW: Store pending subscription if provided
        if (registrationData.subscription_plan) {
          sessionStorage.setItem('pendingSubscription', JSON.stringify(registrationData.subscription_plan));
        }
  
        // Store minimal auth info (no token needed for pending users)
        localStorage.setItem('registrationComplete', 'true');
        
        //  IMPROVED: Handle pending approval navigation
        if (user && user.approval_status === 'pending') {
          // Don't set a token for pending users
          // Just set the user state and let routing handle navigation
          console.log('User registered and pending approval');
          
          // Important: Don't set any access tokens for pending users
          // This prevents them from being treated as authenticated
        }
      }
  
      console.log(' AuthContext: Registration processing complete');
  
      return { 
        user, 
        message, 
        subscription: subscriptionInfo 
      };
    } catch (err) {
      console.error(' Registration error in AuthContext:', err);
  
      let errorMessage = 'Registration failed. Please try again.';
  
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const data = err.response?.data;
        
        console.error(' Registration API error:', {
          status,
          data,
          url: err.config?.url,
          method: err.config?.method
        });
  
        if (status === 400) {
          errorMessage = data?.error || 'Invalid registration data.';
        } else if (status === 409 || status === 422) {
          errorMessage = data?.error || 'Username or email already exists.';
        } else if (data?.error || data?.message) {
          errorMessage = data.error || data.message;
        } else if (err.request) {
          errorMessage = 'Could not connect to server. Please check your internet connection.';
        } else {
          errorMessage = 'An unexpected network error occurred.';
        }
      }
  
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  //  NEW: Terminate all other sessions
  const terminateOtherSessions = async () => {
    try {
      const response = await api.post('/api/auth/sessions/terminate-others');
      return response.data;
    } catch (error) {
      console.error('Failed to terminate other sessions:', error);
      throw new Error(error.response?.data?.error || 'Failed to terminate other sessions');
    }
  };

  //  NEW: Get active sessions
  const getActiveSessions = async () => {
    try {
      const response = await api.get('/api/auth/sessions');
      return response.data.sessions || [];
    } catch (error) {
      console.error('Failed to fetch active sessions:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch sessions');
    }
  };

  //  FIXED: Enhanced stocks API methods with proper error handling and response parsing
  const getStocks = useCallback(async (params = {}) => {
    return withRetry(async () => {
      console.log('AuthContext: Fetching stocks with params:', params);
      
      // Clean and validate parameters (KEEP EXISTING LOGIC)
      const cleanParams = {};
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (value !== null && value !== undefined && value !== '') {
          if (Array.isArray(value) && value.length > 0) {
            cleanParams[key] = value.join(',');
          } else if (typeof value === 'object' && !Array.isArray(value)) {
            if (value.min !== undefined) cleanParams[`${key}_min`] = value.min;
            if (value.max !== undefined) cleanParams[`${key}_max`] = value.max;
          } else {
            cleanParams[key] = value;
          }
        }
      });
  
      console.log('AuthContext: Clean params:', cleanParams);
      
      const response = await api.get('/api/stocks', { 
        params: cleanParams,
        timeout: 120000 //  ADDED: Explicit timeout for this request
      });
      
      // KEEP ALL EXISTING RESPONSE PROCESSING LOGIC
      let responseData = response.data;
      
      if (!responseData || typeof responseData !== 'object') {
        console.error('AuthContext: Invalid response structure:', responseData);
        throw new Error('Invalid response structure from server');
      }
      
      if (!Array.isArray(responseData.stocks)) {
        console.warn('AuthContext: Response missing stocks array, creating empty array');
        responseData.stocks = [];
      } else {
        responseData.stocks = responseData.stocks.map(stock => cleanObjectNaN(stock));
      }
      
      responseData = cleanObjectNaN(responseData);
      
      console.log('AuthContext: Stocks response processed and cleaned:', {
        stocksCount: responseData.stocks?.length || 0,
        hasTotal: responseData.total !== undefined,
        total: responseData.total,
        hasTotalPages: responseData.totalPages !== undefined,
        totalPages: responseData.totalPages,
        firstStock: responseData.stocks?.[0]?.ticker
      });
      
      return responseData;
    }, 2, 3000); // 2 retries with 3 second base delay
  }, []);

  /**
   * Get pending stocks (confidence 50-79%)
   * These stocks are approaching the high confidence threshold
   */
  const getPendingStocks = useCallback(async (params = {}) => {
    return withRetry(async () => {
      console.log(' AuthContext: Fetching pending stocks with params:', params);
      
      // Clean parameters - same logic as getStocks
      const cleanParams = {};
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (value !== null && value !== undefined && value !== '') {
          cleanParams[key] = value;
        }
      });

      console.log(' AuthContext: Cleaned pending stocks params:', cleanParams);
      
      const response = await api.get('/api/stocks/pending', {
        params: cleanParams,
        timeout: 120000 // 2 minutes timeout like getStocks
      });

      // Process response using same logic as getStocks
      let responseData = response.data;
      
      if (!responseData || typeof responseData !== 'object') {
        console.error('AuthContext: Invalid pending stocks response structure:', responseData);
        throw new Error('Invalid response structure from server');
      }
      
      if (!Array.isArray(responseData.stocks)) {
        console.warn('AuthContext: Pending stocks response missing stocks array, creating empty array');
        responseData.stocks = [];
      } else {
        responseData.stocks = responseData.stocks.map(stock => cleanObjectNaN(stock));
      }
      
      responseData = cleanObjectNaN(responseData);
      
      console.log(' AuthContext: Pending stocks response processed:', {
        stocksCount: responseData.stocks?.length || 0,
        total: responseData.total,
        totalPages: responseData.totalPages,
        stats: responseData.stats,
        firstStock: responseData.stocks?.[0]?.ticker
      });
      
      // Log pending-specific breakdown
      if (responseData.stats) {
        console.log(' AuthContext: Pending stocks breakdown:', {
          veryClose: responseData.stats.very_close_count,
          close: responseData.stats.close_count,
          approaching: responseData.stats.approaching_count,
          building: responseData.stats.building_count
        });
      }
      
      return responseData;
    }, 2, 3000); // 2 retries with 3 second base delay, same as getStocks
  }, []);

  const getStockStats = async () => {
    return withRetry(async () => {
      console.log('AuthContext: Fetching stock stats');
      const response = await api.get('/api/stocks/stats', { 
        timeout: 60000 //  ADDED: 1 minute timeout for stats
      });
      return cleanObjectNaN(response.data);
    }, 1, 2000); // 1 retry for stats
  };

  const searchStocks = async (searchTerm) => {
    return withRetry(async () => {
      console.log('AuthContext: Searching stocks with term:', searchTerm);
      
      try {
        const response = await api.get('/api/stocks/search', {
          params: { q: searchTerm },
          timeout: 30000 // 30 second timeout for search
        });
        
        // Log the response for debugging
        console.log('AuthContext API Response:', {
          status: response.status,
          hasData: !!response.data,
          stocksCount: response.data?.stocks?.length || 0,
          found: response.data?.found || 0
        });
        
        // Validate response structure
        if (!response.data) {
          console.error('No data in response');
          return { stocks: [], query: searchTerm, found: 0 };
        }
        
        return cleanObjectNaN(response.data);
        
      } catch (error) {
        // Enhanced error logging
        console.error('AuthContext API Error:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          config: {
            url: error.config?.url,
            params: error.config?.params,
            headers: error.config?.headers
          }
        });
        
        // Re-throw with more context
        if (error.response?.status === 500) {
          const errorMsg = error.response?.data?.error || 'Internal server error';
          throw new Error(`Server error: ${errorMsg}`);
        } else if (error.response?.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (error.response?.status === 403) {
          throw new Error('Access denied. Your account may not be approved.');
        } else if (!error.response) {
          throw new Error('Network error. Please check your connection.');
        } else {
          throw error;
        }
      }
    }, 1, 1000); // 1 retry for search
  };

  const refreshStocks = async () => {
    return withRetry(async () => {
      console.log('AuthContext: Triggering stock refresh');
      const response = await api.post('/api/stocks/refresh', {}, {
        timeout: 180000 //  ADDED: 3 minutes for refresh operations
      });
      return response.data;
    }, 0); // No retries for manual refresh
  };

  const exportStocks = async (filters = {}) => {
    return withRetry(async () => {
      console.log('AuthContext: Exporting stocks with filters:', filters);
      const response = await api.post('/api/stocks/export', filters, {
        timeout: 180000 //  ADDED: 3 minutes for export operations
      });
      return response.data;
    }, 0); // No retries for export
  };

  // Enhanced alerts API methods
  const getAlerts = async (params = {}) => {
    try {
      const cleanParams = {};
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (value !== null && value !== undefined && value !== '') {
          cleanParams[key] = value;
        }
      });

      console.log('AuthContext: Fetching alerts with params:', cleanParams);
      
      const response = await api.get('/api/alerts', { params: cleanParams });
      console.log('AuthContext: Alerts response:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('AuthContext: Error fetching alerts:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch alerts');
    }
  };

  const markAlertAsRead = async (alertId) => {
    try {
      const response = await api.post(`/api/alerts/${alertId}/mark-read`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to mark alert as read');
    }
  };

  const markAllAlertsAsRead = async () => {
    try {
      const response = await api.post('/api/alerts/mark-all-read');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to mark all alerts as read');
    }
  };

  // Admin methods
  const fetchAllUsers = async () => {
    try {
      const response = await api.get('/api/admin/users');
      return response.data.users;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  };

  const approveUser = async (userId, additionalRoles = [], note = '') => {
    try {
      const response = await api.post(`/api/admin/users/${userId}/approve`, {
        roles: additionalRoles,
        note: note
      });
      return response.data;
    } catch (error) {
      console.error('Error approving user:', error);
      throw error;
    }
  };

  const rejectUser = async (userId, reason = '') => {
    try {
      const response = await api.post(`/api/admin/users/${userId}/reject`, {
        reason: reason
      });
      return response.data;
    } catch (error) {
      console.error('Error rejecting user:', error);
      throw error;
    }
  };

  const updateUserRoles = async (userId, roles) => {
    try {
      const response = await api.put(`/api/admin/users/${userId}/roles`, {
        roles: roles
      });
      return response.data;
    } catch (error) {
      console.error('Error updating user roles:', error);
      throw error;
    }
  };

  const getPendingUsers = async () => {
    try {
      const response = await api.get('/api/admin/users/pending');
      return response.data.pending_users || response.data.users || [];
    } catch (error) {
      console.error('Error fetching pending users:', error);
      throw error;
    }
  };

  const getAllUsers = async (params = {}) => {
    try {
      const response = await api.get('/api/admin/users', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching all users:', error);
      throw error;
    }
  };

  const getRoles = async () => {
    try {
      const response = await api.get('/api/admin/roles');
      return response.data.roles || [];
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  };

  const getPermissions = async () => {
    try {
      const response = await api.get('/api/admin/permissions');
      return response.data.permissions || {};
    } catch (error) {
      console.error('Error fetching permissions:', error);
      throw error;
    }
  };

  // Profile and password methods
  const forgotPassword = async (email) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/api/auth/forgot-password', { email });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to send password reset email.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (token, newPassword) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/api/auth/reset-password', {
        token,
        password: newPassword,
      });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to reset password.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const confirmEmail = async (token) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/api/auth/confirm-email', { token });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Email confirmation failed.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (profileData) => {
    setLoading(true);
    setError(null);
  
    try {
      // Use the correct endpoint
      const response = await api.put('/api/users/profile', profileData);
      const updatedUser = response.data.user;
      setCurrentUser(updatedUser);
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (err) {
      console.error('Profile update error:', err);
      const errorMessage = err.response?.data?.error || 'Failed to update profile.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/api/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to change password.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  // Utility methods
  const isAdmin = () => {
    return currentUser?.roles?.includes('admin') || false;
  };

  const hasPermission = (permission) => {
    if (isAdmin()) return true;
    return currentUser?.permissions?.includes(permission) || false;
  };

  const hasRole = (role) => {
    return currentUser?.roles?.includes(role) || false;
  };

  const isApproved = () => {
    return currentUser?.approval_status === 'approved';
  };

  const isPending = () => {
    return currentUser?.approval_status === 'pending';
  };

  const isRejected = () => {
    return currentUser?.approval_status === 'rejected';
  };

  const getFullName = () => {
    if (!currentUser) return '';
    return `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim();
  };

  const getDisplayName = () => {
    const fullName = getFullName();
    return fullName || currentUser?.username || 'User';
  };

  //  NEW: Get session info
  const getSessionInfo = () => {
    const sessionInfo = localStorage.getItem('session_info');
    return sessionInfo ? JSON.parse(sessionInfo) : null;
  };

  //  NEW: Check if user can access protected routes
  const canAccessProtectedRoutes = useCallback(() => {
    if (!currentUser) return false;
    
    // Admin always has access
    if (currentUser.username === 'Administrator' || currentUser.roles?.includes('admin')) {
      return true;
    }
    
    // Check approval status
    const isApproved = currentUser.is_approved === true || 
                       currentUser.approval_status === 'approved';
    
    // Check if account is active
    const isActive = currentUser.is_active !== false;
    
    return isApproved && isActive;
  }, [currentUser]);

  //  NEW: Get user status for routing decisions
  const getUserStatus = useCallback(() => {
    if (!currentUser) return 'unauthenticated';
    
    if (currentUser.approval_status === 'pending') return 'pending';
    if (currentUser.approval_status === 'rejected') return 'rejected';
    if (currentUser.is_active === false) return 'inactive';
    
    const isApproved = currentUser.is_approved === true || 
                       currentUser.approval_status === 'approved' ||
                       currentUser.username === 'Administrator';
    
    return isApproved ? 'active' : 'unknown';
  }, [currentUser]);



  const verifyPaymentAndRefresh = async (sessionId) => {
    try {
      console.log(' AuthContext: Verifying payment status for session:', sessionId);
      
      // First, verify payment with backend
      const verifyResponse = await api.post('/api/stripe/verify-payment-status', {
        session_id: sessionId
      });
      
      if (verifyResponse.data.subscription_active) {
        console.log(' AuthContext: Payment verified, forcing user data refresh');
        
        // Force refresh user profile multiple times with delays
        for (let i = 0; i < 3; i++) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          await fetchUserProfile(true);
          await checkSubscription(true);
          
          // Check if subscription is now active
          if (currentUser?.subscription?.status === 'active') {
            console.log(' AuthContext: Subscription now active!');
            return true;
          }
        }
      }
      
      return false;
    } catch (error) {
      console.error(' AuthContext: Payment verification failed:', error);
      return false;
    }
  };

  const value = {
    // State
    currentUser,
    loading,
    error,
    
    // Authentication methods
    login,
    register,
    logout,
    fetchUserProfile,
    refreshSubscriptionStatus,
    checkForPotentialAutoApproval,
    verifyPaymentAndRefresh,

    //  NEW: Subscription methods
    checkSubscription,
    startTrial,
    getPaddleSubscriptionStatus,
    cancelSubscription,
    
    // Password methods
    forgotPassword,
    resetPassword,
    changePassword,
    
    // Email methods
    confirmEmail,
    
    // Profile methods
    updateProfile,
    
    //  NEW: Session management methods
    terminateOtherSessions,
    getActiveSessions,
    getSessionInfo,
    
    // Utility methods
    clearError,
    isAdmin,
    hasPermission,
    hasRole,
    isApproved,
    isPending,
    isRejected,
    getFullName,
    getDisplayName,
    
    //  NEW: Enhanced utility methods
    clearAuthState,
    validateUserState,
    canAccessProtectedRoutes,
    getUserStatus,
    
    // FIXED: Enhanced API methods
    getStocks,
    getPendingStocks,
    getStockStats,
    searchStocks,
    refreshStocks,
    exportStocks,
    getAlerts,
    markAlertAsRead,
    markAllAlertsAsRead,
    
    // Admin Dashboard Methods
    fetchAllUsers,
    getAllUsers,
    getPendingUsers,
    approveUser,
    rejectUser,
    updateUserRoles,
    getRoles,
    getPermissions,
    
    // API instance for direct use in components
    api,
    forceRefreshUserData: () => fetchUserProfile(true),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};